/**
 * This simple type specifies the preset shape geometry that is to be used for a
 * shape. An enumeration of this simple type is used so that a custom geometry
 * does not have to be specified but instead can be constructed automatically by
 * the generating application. For each enumeration listed there is also the
 * corresponding DrawingML code that would be used to construct this shape were
 * it a custom geometry. Within the construction code for each of these preset
 * shapes there are predefined guides that the generating application shall
 * maintain for calculation purposes at all times. The necessary guides should
 * have the following values.
 *
 * REF: ECMA-376, Third Edition, Part 1 | Section: 20.1.10.55
 *
 * 3/4 of a Circle ('3cd4') - Constant value of "16200000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 270 degrees.
 *
 * 3/8 of a Circle ('3cd8') - Constant value of "8100000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 135 degrees.
 *
 * 5/8 of a Circle ('5cd8') - Constant value of "13500000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 225 degrees.
 *
 * 7/8 of a Circle ('7cd8') - Constant value of "18900000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 315 degrees.
 *
 * Shape Bottom Edge ('b') - Constant value of "h"
 * This is the bottom edge of the shape and since the top edge of the shape is
 * considered the 0 point, the bottom edge is thus the shape height.
 *
 * 1/2 of a Circle ('cd2') - Constant value of "10800000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 180 degrees.
 *
 * 1/4 of a Circle ('cd4') - Constant value of "5400000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 90 degrees.
 *
 * 1/8 of a Circle ('cd8') - Constant value of "2700000.0"
 * The units here are in 60,000ths of a degree. This is equivalent to 45 degrees.
 *
 * Shape Height ('h')
 * This is the variable height of the shape defined in the shape properties.
 * This value is received from the shape transform listed within the <spPr> element.
 *
 * Horizontal Center ('hc') - Calculated value of "* / w 1.0 2.0"
 * This is the horizontal center of the shape which is just the width divided by 2.
 *
 * 1 / 2 of Shape Height('hd2') - Calculated value of "* / h 1.0 2.0"
 * This is 1 / 2 the shape height.
 *
 * 1 / 4 of Shape Height('hd4') - Calculated value of "* / h 1.0 4.0"
 * This is 1 / 4 the shape height.
 *
 * 1 / 5 of Shape Height('hd5') - Calculated value of "* / h 1.0 5.0"
 * This is 1 / 5 the shape height.
 *
 * 1/6 of Shape Height ('hd6') - Calculated value of "* / h 1.0 6.0"
 * This is 1 / 6 the shape height.
 *
 * 1 / 8 of Shape Height('hd8') - Calculated value of "* / h 1.0 8.0"
 * This is 1 / 8 the shape height.
 *
 * Shape Left Edge('l') - Constant value of "0"
 * This is the left edge of the shape and the left edge of the shape is
 * considered the horizontal 0 point.
 *
 * Longest Side of Shape('ls') - Calculated value of "max w h"
 * This is the longest side of the shape.This value is either the width or the
 * height depending on which is greater.
 *
 * Shape Right Edge('r') - Constant value of "w"
 * This is the right edge of the shape and since the left edge of the shape is
 * considered the 0 point, the right edge is thus the shape width.
 *
 * Shortest Side of Shape('ss') - Calculated value of "min w h"
 * This is the shortest side of the shape.This value is either the width or the
 * height depending on which is smaller.
 *
 * 1 / 2 Shortest Side of Shape('ssd2') - Calculated value of "* / ss 1.0 2.0"
 * This is 1 / 2 the shortest side of the shape.
 *
 * 1 / 4 Shortest Side of Shape('ssd4') - Calculated value of "* / ss 1.0 4.0"
 * This is 1 / 4 the shortest side of the shape.
 *
 * 1 / 6 Shortest Side of Shape('ssd6') - Calculated value of "* / ss 1.0 6.0"
 * This is 1 / 6 the shortest side of the shape.
 *
 * 1 / 8 Shortest Side of Shape('ssd8') - Calculated value of "* / ss 1.0 8.0"
 * This is 1 / 8 the shortest side of the shape.
 *
 * Shape Top Edge('t') - Constant value of "0"
 * This is the top edge of the shape and the top edge of the shape is considered
 * the vertical 0 point.
 *
 * Vertical Center of Shape('vc') - Calculated value of "* / h 1.0 2.0"
 * This is the vertical center of the shape which is just the height divided by 2.
 *
 * Shape Width('w')
 * This is the variable width of the shape defined in the shape properties.This
 * value is received from the shape transform listed within the < spPr > element.
 *
 * 1 / 2 of Shape Width('wd2') - Calculated value of "* / w 1.0 2.0"
 * This is 1 / 2 the shape width.
 *
 * 1 / 4 of Shape Width('wd4') - Calculated value of "* / w 1.0 4.0"
 * This is 1 / 4 the shape width.
 *
 * 1 / 5 of Shape Width('wd5') - Calculated value of "* / w 1.0 5.0"
 * This is 1 / 5 the shape width.
 *
 * 1 / 6 of Shape Width('wd6') - Calculated value of "* / w 1.0 6.0"
 * This is 1 / 6 the shape width.
 *
 * 1 / 8 of Shape Width('wd8') - Calculated value of "* / w 1.0 8.0"
 * This is 1/8 the shape width.
 *
 * 1/10 of Shape Width ('wd10') - Calculated value of "*  w 1.0 10.0"
 * This is 1 / 10 the shape width.
 */

export enum ShapeType {
    cross = "cross",
    ellipse = "ellipse",
    line = "line",
    pentagon = "pentagon",
    rect = "rect",
    rightArrow = "rightArrow",
    roundRect = "roundRect",
    placeholder = "placeholder",
    star5 = "star5",
    triangle = "triangle",
    wedgeRectCallout = "wedgeRectCallout",
    checkmark = "checkmark",

    // Keeping these shapes (not commenting out) as definitions are in repository
    heart = "heart",
    swooshArrow = "swooshArrow",

    // Commenting out below shapes as corresponding definitions are not in repository.
    // Keeping in commented form for reference purpose. Their definitions can be added in repository by following
    // corresponding xml definitions found in below location from specification.
    // ECMA-376, Third Edition, Part 1 - Fundamentals And Markup Language Reference
    // OfficeOpenXML-DrawingMLGeometries/presetShapeDefinitions.xml

    // lineInv = "lineInv",
    // rtTriangle = "rtTriangle",
    // crossmark = "crossmark",
    // diamond = "diamond",
    // parallelogram = "parallelogram",
    // trapezoid = "trapezoid",
    // nonIsoscelesTrapezoid = "nonIsoscelesTrapezoid",
    // hexagon = "hexagon",
    // heptagon = "heptagon",
    // octagon = "octagon",
    // decagon = "decagon",
    // dodecagon = "dodecagon",
    // star4 = "star4",
    // star6 = "star6",
    // star7 = "star7",
    // star8 = "star8",
    // star10 = "star10",
    // star12 = "star12",
    // star16 = "star16",
    // star24 = "star24",
    // star32 = "star32",
    // round1Rect = "round1Rect",
    // round2SameRect = "round2SameRect",
    // round2DiagRect = "round2DiagRect",
    // snipRoundRect = "snipRoundRect",
    // snip1Rect = "snip1Rect",
    // snip2SameRect = "snip2SameRect",
    // snip2DiagRect = "snip2DiagRect",
    // plaque = "plaque",
    // teardrop = "teardrop",
    // homePlate = "homePlate",
    // chevron = "chevron",
    // pieWedge = "pieWedge",
    // pie = "pie",
    // blockArc = "blockArc",
    // donut = "donut",
    // noSmoking = "noSmoking",
    // leftArrow = "leftArrow",
    // upArrow = "upArrow",
    // downArrow = "downArrow",
    // stripedRightArrow = "stripedRightArrow",
    // notchedRightArrow = "notchedRightArrow",
    // bentUpArrow = "bentUpArrow",
    // leftRightArrow = "leftRightArrow",
    // upDownArrow = "upDownArrow",
    // leftUpArrow = "leftUpArrow",
    // leftRightUpArrow = "leftRightUpArrow",
    // quadArrow = "quadArrow",
    // leftArrowCallout = "leftArrowCallout",
    // rightArrowCallout = "rightArrowCallout",
    // upArrowCallout = "upArrowCallout",
    // downArrowCallout = "downArrowCallout",
    // leftRightArrowCallout = "leftRightArrowCallout",
    // upDownArrowCallout = "upDownArrowCallout",
    // quadArrowCallout = "quadArrowCallout",
    // bentArrow = "bentArrow",
    // uturnArrow = "uturnArrow",
    // circularArrow = "circularArrow",
    // leftCircularArrow = "leftCircularArrow",
    // leftRightCircularArrow = "leftRightCircularArrow",
    // curvedRightArrow = "curvedRightArrow",
    // curvedLeftArrow = "curvedLeftArrow",
    // curvedUpArrow = "curvedUpArrow",
    // curvedDownArrow = "curvedDownArrow",
    // cube = "cube",
    // can = "can",
    // "1773 lightningBolt" = "1773 lightningBolt",
    // sun = "sun",
    // moon = "moon",
    // smileyFace = "smileyFace",
    // irregularSeal1 = "irregularSeal1",
    // irregularSeal2 = "irregularSeal2",
    // foldedCorner = "foldedCorner",
    // bevel = "bevel",
    // frame = "frame",
    // halfFrame = "halfFrame",
    // corner = "corner",
    // diagStripe = "diagStripe",
    // chord = "chord",
    // arc = "arc",
    // leftBracket = "leftBracket",
    // rightBracket = "rightBracket",
    // leftBrace = "leftBrace",
    // rightBrace = "rightBrace",
    // bracketPair = "bracketPair",
    // bracePair = "bracePair",
    // straightConnector1 = "straightConnector1",
    // bentConnector2 = "bentConnector2",
    // bentConnector3 = "bentConnector3",
    // bentConnector4 = "bentConnector4",
    // bentConnector5 = "bentConnector5",
    // curvedConnector2 = "curvedConnector2",
    // curvedConnector3 = "curvedConnector3",
    // curvedConnector4 = "curvedConnector4",
    // curvedConnector5 = "curvedConnector5",
    // callout1 = "callout1",
    // callout2 = "callout2",
    // callout3 = "callout3",
    // accentCallout1 = "accentCallout1",
    // accentCallout2 = "accentCallout2",
    // accentCallout3 = "accentCallout3",
    // borderCallout1 = "borderCallout1",
    // borderCallout2 = "borderCallout2",
    // borderCallout3 = "borderCallout3",
    // accentBorderCallout1 = "accentBorderCallout1",
    // accentBorderCallout2 = "accentBorderCallout2",
    // accentBorderCallout3 = "accentBorderCallout3",
    // wedgeRoundRectCallout = "wedgeRoundRectCallout",
    // wedgeEllipseCallout = "wedgeEllipseCallout",
    // cloudCallout = "cloudCallout",
    // cloud = "cloud",
    // ribbon = "ribbon",
    // ribbon2 = "ribbon2",
    // ellipseRibbon = "ellipseRibbon",
    // ellipseRibbon2 = "ellipseRibbon2",
    // leftRightRibbon = "leftRightRibbon",
    // verticalScroll = "verticalScroll",
    // "1826 horizontalScroll" = "1826 horizontalScroll",
    // wave = "wave",
    // doubleWave = "doubleWave",
    // plus = "plus",
    // flowChartProcess = "flowChartProcess",
    // flowChartDecision = "flowChartDecision",
    // flowChartInputOutput = "flowChartInputOutput",
    // flowChartPredefinedProcess = "flowChartPredefinedProcess",
    // flowChartInternalStorage = "flowChartInternalStorage",
    // flowChartDocument = "flowChartDocument",
    // flowChartMultidocument = "flowChartMultidocument",
    // flowChartTerminator = "flowChartTerminator",
    // flowChartPreparation = "flowChartPreparation",
    // flowChartManualInput = "flowChartManualInput",
    // flowChartManualOperation = "flowChartManualOperation",
    // flowChartConnector = "flowChartConnector",
    // flowChartPunchedCard = "flowChartPunchedCard",
    // flowChartPunchedTape = "flowChartPunchedTape",
    // flowChartSummingJunction = "flowChartSummingJunction",
    // flowChartOr = "flowChartOr",
    // flowChartCollate = "flowChartCollate",
    // flowChartSort = "flowChartSort",
    // flowChartExtract = "flowChartExtract",
    // flowChartMerge = "flowChartMerge",
    // flowChartOfflineStorage = "flowChartOfflineStorage",
    // flowChartOnlineStorage = "flowChartOnlineStorage",
    // flowChartMagneticTape = "flowChartMagneticTape",
    // flowChartMagneticDisk = "flowChartMagneticDisk",
    // flowChartMagneticDrum = "flowChartMagneticDrum",
    // flowChartDisplay = "flowChartDisplay",
    // flowChartDelay = "flowChartDelay",
    // flowChartAlternateProcess = "flowChartAlternateProcess",
    // flowChartOffpageConnector = "flowChartOffpageConnector",
    // actionButtonBlank = "actionButtonBlank",
    // actionButtonHome = "actionButtonHome",
    // actionButtonHelp = "actionButtonHelp",
    // actionButtonInformation = "actionButtonInformation",
    // actionButtonForwardNext = "actionButtonForwardNext",
    // actionButtonBackPrevious = "actionButtonBackPrevious",
    // actionButtonEnd = "actionButtonEnd",
    // actionButtonBeginning = "actionButtonBeginning",
    // actionButtonReturn = "actionButtonReturn",
    // actionButtonDocument = "actionButtonDocument",
    // actionButtonSound = "actionButtonSound",
    // actionButtonMovie = "actionButtonMovie",
    // gear6 = "gear6",
    // gear9 = "gear9",
    // funnel = "funnel",
    // mathPlus = "mathPlus",
    // mathMinus = "mathMinus",
    // mathMultiply = "mathMultiply",
    // mathDivide = "mathDivide",
    // mathEqual = "mathEqual",
    // "1879 mathNotEqual" = "1879 mathNotEqual",
    // cornerTabs = "cornerTabs",
    // squareTabs = "squareTabs",
    // plaqueTabs = "plaqueTabs",
    // chartX = "chartX",
    // chartStar = "chartStar",
    // chartPlus = "chartPlus",
}
